import { Dropdown, IDropdownOption, ITooltipProps, Label, MessageBar, MessageBarType, Panel, PanelType, Pivot, PivotItem, Spinner, Stack, TextField, Tooltip } from "office-ui-fabric-react";
import * as React from "react";
import { IBooking, IMyBooking } from "../../../types/Booking.types";
import { Event, View, Calendar, momentLocalizer, CalendarProps, EventProps } from "react-big-calendar";
import { getSiteRoomBookings } from "../../../api/rooms";
import { getMySites } from "../../../api/siteslocations";
import moment from "moment";

import styles from "./SiteCalendar.module.scss";

export interface ISiteCalendarProps {
    onDismiss: () => void;
}

const localizer = momentLocalizer(moment);

const getDateRangeFromDate = (date: Date, view: View) => {
    const baseDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    let startDate = new Date(baseDate), endDate = new Date(baseDate);
    if (view === "month") {
        const month = startDate.getMonth();
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 1, 23, 59, 59);
        endDate.setDate(endDate.getDate() - 1);
        if (startDate.getDate() > 1) {
            startDate.setDate(1);
        }
    } else if (view === "agenda") {
        endDate = new Date(startDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate(), 23, 59, 59);
    } else if (view === "week" || view === "work_week") {
        while (startDate.getDay() > 0) {
            startDate.setDate(startDate.getDate() - 1);
        }
        while (endDate.getDay() < 6) {
            endDate.setDate(endDate.getDate() + 1);
        }
    } else {
        endDate.setHours(23);
        endDate.setMinutes(59);
        endDate.setSeconds(59);
    }
    return {
        startDate, endDate
    };
}

export const SiteCalendar = (props: ISiteCalendarProps) => {
    const [ sites, setSites ] = React.useState<{SiteId: number, SiteName: string}[]>();
    const [ selectedSite, setSelectedSite ] = React.useState<number>();
    const [ bookings, setBookings ] = React.useState<IMyBooking[]>();
    const [ view, setView ] = React.useState<View>("agenda");
    const [ calendarDate, setCalendarDate ] = React.useState<Date>(new Date());
    const [ loading, setLoading ] = React.useState(true);
    const [ filter, setFilter ] = React.useState<string>();
    const [ tooltipProps, setTooltipProps ] = React.useState<ITooltipProps>();

    const _loadBookings = React.useCallback(async (abortController: AbortController) => {
        if (!selectedSite) return;
        const { startDate, endDate } = getDateRangeFromDate(calendarDate, view);
        const response = await getSiteRoomBookings({ siteId: selectedSite, fromDate: startDate, toDate: endDate}, abortController);
        if (response.ok) {
            setBookings(await response.json());
        }
        if (loading) setLoading(false);
    }, [calendarDate, selectedSite, view]);

    const _loadSites = React.useCallback(async (abortController: AbortController) => {
        const response = await getMySites();
        if (response.ok) {
            const siteJson = await response.json();
            setSites(siteJson);
            if (siteJson.length === 1) {
                setSelectedSite(siteJson[0].SiteId);
            }
        }
    }, []);

    const _onInit = React.useCallback(async () => {
        const sitesAbort = new AbortController();
        await _loadSites(sitesAbort);

        setLoading(false);
    }, [_loadBookings, _loadSites]);

    React.useEffect(() => {
        _onInit();
    }, []);

    React.useEffect(() => {
        const abort = new AbortController();
        _loadBookings(abort);
    }, [_loadBookings, calendarDate, selectedSite]);

    let events: Event[] = [];
    if (bookings && bookings.length) {
        events = bookings.map((booking) => {
            let title: string;
            if (Boolean(booking.Description)) {
                title = booking.Description;
            } else {
                title = Boolean(booking.OnBehalfOfEmail) ? booking.OnBehalfOfEmail : booking.BookedByUserName;
            }
            if (title.length > 100) {
                title = title.substring(0,100) + '...';
            }
            return {
                allDay: false,
                start: new Date(booking.Start),
                end: new Date(booking.Finish),
                title,
                data: booking
            } as Event;
        });
    }

    if (filter && filter.trim() !== "") {
        events = events.filter((e) => {
            const regex = new RegExp(
                filter.replace(".", "\.")
                    .replace("$", "\$")
                    .replace("^", "\^"),
             "i");
            const booking = (e as any).data as IMyBooking;
            return regex.test(booking.Description) || 
                regex.test(booking.BookedByUserName) ||
                regex.test(booking.OnBehalfOfEmail) ||
                regex.test(booking.SiteName) ||
                regex.test(booking.LocationName) ||
                regex.test(booking.RoomName);
        });
    }

    return (
        <Panel 
        onDismiss={props.onDismiss}
        className={styles.panel}
        styles={{
            scrollableContent: {
                height: "100%"
            },
            content: {
                height: "calc(100% - 100px)"
            }
        }} type={PanelType.large} isOpen={true} headerText="Site Calendar">
            <MessageBar messageBarType={MessageBarType.info}>
                By default, the agenda view shows bookings from today for the upcoming month. Use the navigation options to view other dates, or view results as a calendar. Use the filter to narrow results. If you are struggling to read busy days/slots in the calendar views, please use the Agenda view
            </MessageBar>
            { sites && sites.length > 1 && (
                <Dropdown label="Select a Site"
                    options={sites?.map((site) => ({
                        key: site.SiteId,
                        text: site.SiteName
                    } as IDropdownOption)) || []}
                    selectedKey={selectedSite}
                    onChange={(e, option) => {
                        if (option) setSelectedSite(option.key as number);
                    }}
                />
            )}
            { loading === true && (
                <Spinner label="Loading Bookings" />
            )}
            { selectedSite !== undefined && (
                <>
                    <TextField styles={{root: { marginTop: 10 }}} value={filter} onChange={(e, newValue) => { setFilter(newValue) }} 
                        placeholder="Filter events by typing here ie. room name, location, user's name or email"></TextField>
                    <Calendar 
                        date={calendarDate}
                        localizer={localizer}
                        view={view}
                        events={events}
                        onNavigate={(newDate, newView) => {
                            setLoading(true);
                            setCalendarDate(new Date(newDate));
                            setView(newView);
                        }}
                        onView={(newView) => {
                            setLoading(true);
                            setView(newView);
                            if (newView === "agenda") {
                                setCalendarDate(new Date());
                            }
                        }}
                        onSelectEvent={(calendarEvent, e) => {
                            const booking = (calendarEvent as any).data as IMyBooking;
                            const target = e.currentTarget as HTMLDivElement;
                            const where = <div><strong>Where:</strong> {booking.RoomName}, {booking.LocationName}, {booking.SiteName}</div>;
                            const content = <Stack>
                                <div><strong>Booked By:</strong> {booking.BookedByUserName}</div>
                                { Boolean(booking.OnBehalfOfEmail) && <div><strong>On Behalf Of:</strong> {booking.OnBehalfOfEmail}</div> }
                                { where }
                                { Boolean(booking.Description) && <div style={{whiteSpace: "pre-wrap"}}><strong>Description:</strong><br/>{booking.Description}</div> }
                            </Stack>;
                            setTooltipProps({
                                targetElement: target,
                                calloutProps: {
                                    onDismiss: () => { setTooltipProps(undefined) }
                                },
                                content, 
                            });
                        }}
                        drilldownView="day"
                        components={{
                            agenda: {
                                event: (props: EventProps<Event>) => {
                                    const booking = (props.event as any).data as IMyBooking;
                                    if (!booking) return null;
                                    return (
                                        <>
                                            <div className={styles.parent}>
                                                <Stack horizontal verticalAlign="center" tokens={{childrenGap: 10}} className={styles.titleRow}>
                                                    <Label>Title/Description:</Label>
                                                    <div>{booking.Description || "N/A"}</div>
                                                </Stack>
                                                <div className={styles.bookedBy}>
                                                    <Label>Booked By:</Label>
                                                    <div>{booking.BookedByUserName}</div>
                                                </div>
                                                <div className={styles.onBehalfOf}>
                                                    <Label style={{flexBasis: 90, flexShrink: 0}}>On Behalf Of:</Label>
                                                    <div>{booking.OnBehalfOfEmail}</div>
                                                </div>
                                                <div className={styles.where}>
                                                    <Label>Where:</Label>
                                                    <div>{`${booking.RoomName}, ${booking.LocationName}`}</div>
                                                </div>
                                            </div>
                                        </>
                                    );
                                }
                            }
                        }}
                        style={{marginTop: 20}}
                    />
                </>
            )}
            { tooltipProps !== undefined && (
                <Tooltip {...tooltipProps} />
            )}
        </Panel>
    )
}

export default SiteCalendar;